import React from 'react';
import { Descriptions, Space, Spin, Typography } from 'antd';
import { toast } from '../../../lib/toast';
import Drawer from '../../../components/AppDrawer';
import { CopyOutlined } from '@ant-design/icons';
import type { ContactDetail } from '../types';
import { contactTypeLabel } from '../contactType';
import { normalizeHttpUrl } from '../../../lib/url';
import { useContactLeadsSummary } from '../hooks/useContactLeadsSummary';
import ContactTasksPanel from './ContactTasksPanel';
import AuditProperties from '../../../components/AuditProperties';
import Can from '../../permissions/Can';

const { Text } = Typography;

const CONTACT_ROLE_LABEL: Record<number, string> = {
  0: 'Agent',
  1: 'Sales',
};

function copyToClipboard(text: string) {
  void navigator.clipboard
    .writeText(text)
    .then(() => {
      toast.success('Copied!');
    })
    .catch(() => {
      toast.error('Copy failed');
    });
}

function formatPhoneForTel(phone: string): string {
  const cleaned = phone.trim().replace(/[^\d+]/g, '');
  if (cleaned.startsWith('+')) return `+${cleaned.slice(1).replace(/\+/g, '')}`;
  return cleaned.replace(/\+/g, '');
}

export interface ContactViewDrawerProps {
  open: boolean;
  onClose: () => void;
  contact: ContactDetail | null | undefined;
  loading: boolean;
  onEdit: () => void;
}

const ContactViewDrawer: React.FC<ContactViewDrawerProps> = ({
  open,
  onClose,
  contact,
  loading,
  onEdit,
}) => {

  const { data: leadsSummary, isLoading: loadingLeadsSummary } = useContactLeadsSummary(
    contact?.id ?? null
  );

  return (
    <Drawer
      title="Contact details"
      placement="right"
      width={800}
      open={open}
      onClose={onClose}
        extra={
          <Can permission="contacts.edit">
            <Typography.Link onClick={onEdit} style={{ marginRight: 8 }}>
              Edit
            </Typography.Link>
          </Can>
        }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : contact == null ? (
        <Text type="secondary">Contact not found.</Text>
      ) : (
        <>
          <div className="cp-form-section">
            <Descriptions column={2} size="small" title="Basic information">
              <Descriptions.Item label="Category" span={2}>
                {contact.categoryLabel ?? ''}
              </Descriptions.Item>
              <Descriptions.Item label="Type" span={2}>
                {contactTypeLabel(contact.contactType)}
              </Descriptions.Item>
              <Descriptions.Item label="Name">{contact.name ?? ''}</Descriptions.Item>
              <Descriptions.Item label="Company name">
                {contact.companyName ?? ''}
              </Descriptions.Item>
              <Descriptions.Item label="Job title">{contact.jobTitle ?? ''}</Descriptions.Item>
              <Descriptions.Item label="Role">{contact.role != null ? CONTACT_ROLE_LABEL[contact.role] ?? contact.role : '—'}</Descriptions.Item>
            </Descriptions>
          </div>

          <div className="cp-form-section">
            <Descriptions column={2} size="small" title="Communication">
              <Descriptions.Item label="Mobile">
                {contact.mobileNumber ? (
                  <Space size={4}>
                    <a href={`tel:${formatPhoneForTel(contact.mobileNumber)}`}>{contact.mobileNumber}</a>
                    <CopyOutlined
                      style={{ cursor: 'pointer', opacity: 0.5, fontSize: 11 }}
                      onClick={() => copyToClipboard(contact.mobileNumber!)}
                    />
                  </Space>
                ) : (
                  ''
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {contact.email ? (
                  <Space size={4}>
                    <a href={`mailto:${contact.email}`}>{contact.email}</a>
                    <CopyOutlined
                      style={{ cursor: 'pointer', opacity: 0.5, fontSize: 11 }}
                      onClick={() => copyToClipboard(contact.email!)}
                    />
                  </Space>
                ) : (
                  ''
                )}
              </Descriptions.Item>
              <Descriptions.Item label="WhatsApp">{contact.whatsAppNumber ?? ''}</Descriptions.Item>
              <Descriptions.Item label="Website">
                {contact.website ? (
                  <a href={normalizeHttpUrl(contact.website) ?? '#'} target="_blank" rel="noopener noreferrer">
                    {contact.website}
                  </a>
                ) : (
                  ''
                )}
              </Descriptions.Item>
              <Descriptions.Item label="LinkedIn">{contact.linkedIn ?? ''}</Descriptions.Item>
            </Descriptions>
          </div>

          <div className="cp-form-section">
            <Descriptions column={2} size="small" title="Location">
              <Descriptions.Item label="Address" span={2}>
                {contact.address ?? ''}
              </Descriptions.Item>
              <Descriptions.Item label="Google Maps link">
                {contact.googleMapsLink ? (
                  <a href={contact.googleMapsLink} target="_blank" rel="noopener noreferrer">
                    Open map
                  </a>
                ) : (
                  ''
                )}
              </Descriptions.Item>
            </Descriptions>
          </div>
          {contact.notes && (
            <div className="cp-form-section">
              <Text strong>Notes</Text>
              <div style={{ marginTop: 4 }}>
                <Text type="secondary">{contact.notes}</Text>
              </div>
            </div>
          )}

          <div className="cp-form-section">
            <ContactTasksPanel contactId={contact.id} />
          </div>

          <div className="cp-form-section">
            <Descriptions column={2} size="small" title="Leads summary">
              <Descriptions.Item label="Total leads">
                {loadingLeadsSummary ? <Spin size="small" /> : leadsSummary?.totalLeads ?? 0}
              </Descriptions.Item>
            </Descriptions>
          </div>

          <AuditProperties
            className="cp-form-section"
            values={{
              createdAtUtc: contact.createdAtUtc,
              createdByDisplayName: contact.createdByDisplayName,
              createdByUserId: contact.createdByUserId,
              lastModifiedAtUtc: contact.lastModifiedAtUtc,
              modifiedByDisplayName: contact.modifiedByDisplayName,
              modifiedByUserId: contact.modifiedByUserId,
            }}
          />
        </>
      )}
    </Drawer>
  );
};

export default ContactViewDrawer;
